import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getRazorpayPublicKey } from "@/lib/razorpay-public.functions";

const SCRIPT_SRC = "https://cdn.razorpay.com/widgets/affordability/affordability.js";
// Razorpay's default target id per the affordability-widget docs.
const CONTAINER_ID = "razorpay-affordability-widget";

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (typeof window.RazorpayAffordabilitySuite === "function") return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("script error")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("script error"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

let cachedKey: string | null | undefined;

interface Props {
  amountPaise: number;
}

export function RazorpayAffordabilityWidget({ amountPaise }: Props) {
  const fetchKey = useServerFn(getRazorpayPublicKey);
  const [keyId, setKeyId] = useState<string | null>(cachedKey ?? null);
  const instanceRef = useRef<any>(null);

  useEffect(() => {
    if (cachedKey !== undefined) {
      if (cachedKey !== keyId) setKeyId(cachedKey);
      return;
    }
    let alive = true;
    fetchKey()
      .then((res) => {
        cachedKey = res.keyId;
        if (alive) setKeyId(res.keyId);
      })
      .catch(() => {
        cachedKey = null;
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  useEffect(() => {
    if (!keyId || amountPaise <= 0) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled) return;
        const Ctor = window.RazorpayAffordabilitySuite;
        if (typeof Ctor !== "function") return;
        const el = document.getElementById(CONTAINER_ID);
        if (el) el.innerHTML = "";
        try {
          const suite = new Ctor({
            key: keyId,
            amount: Math.round(amountPaise),
          });
          suite.render?.();
          instanceRef.current = suite;
        } catch {
          // Widget failed to mount — fail silent, price still shows.
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      const el = document.getElementById(CONTAINER_ID);
      if (el) el.innerHTML = "";
      instanceRef.current = null;
    };
  }, [keyId, amountPaise]);

  // Razorpay's Affordability Widget silently hides itself when the amount is
  // below the EMI/BNPL eligibility floor (~₹3,000) or when no offers apply
  // (e.g. everything sold out / no live variants). Show a static fallback so
  // shoppers always see the payment-flexibility message.
  const belowThreshold = amountPaise > 0 && amountPaise < 300000;

  if (!keyId || amountPaise <= 0 || belowThreshold) {
    return (
      <div className="mt-3">
        <p className="text-[12px] leading-snug text-muted-foreground">
          💳 No-Cost EMI, Pay Later & cardless offers available on orders above{" "}
          <span className="font-medium text-brand-deep">₹3,000</span> at checkout.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div id={CONTAINER_ID} />
      <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
        * Terms and conditions apply. Available only on orders above ₹3,000.
      </p>
    </div>
  );
}


export default RazorpayAffordabilityWidget;
